// Backlog 270 — entity-kind-aware CLI verbs.
//
// Real-user audit (R4, 2026-05-23) found 23 events of "ran a runtime-only
// verb against an inspiration" across three users in a 30-day window. The
// platform answer is K8s-shaped (`NOT_A_RUNTIME` / `RUNTIME_NOT_FOUND`);
// the user-shaped answer is "you reached for the wrong verb — try this
// one instead."
//
// This module is the single place the CLI looks an entity up regardless
// of which realm it lives in, plus the per-verb redirect copy that points
// the user at the correct command. Verbs with runtime-only semantics
// (`unplug`, `logs`, `dev`, `ssh`, `pull`, ...) call `assertEntityIsRuntime`
// BEFORE they run their own logic; on a kind mismatch the user sees a
// §23-voiced sentence pointing at the right next step.

import { V2ApiError } from '../api/v2-errors.js';
import { CloudGridError } from '../utils.js';
import type { IV2Client } from '../api/v2-client.js';

export type EntityRealm = 'runtime' | 'inspiration';

export interface EntityKindInfo {
  entity_id: string;
  name: string;
  display_name: string;
  realm: EntityRealm;
  kind: 'app' | 'agent' | 'inspiration';
  /**
   * Wire-side status when the entity is a runtime: `'charged' | 'live' |
   * 'live_unhealthy' | 'stopped' | 'archived' | 'expired' | 'failed'`.
   * Inspirations don't carry a meaningful status here (the inspiration
   * endpoint returns one but it's not used by the kind-aware fallbacks).
   */
  status?: string;
}

/**
 * Look an entity up by slug, name, or entity_id without caring which realm
 * it lives in. Tries the runtimes endpoint first; on `NOT_A_RUNTIME` falls
 * back to the inspirations endpoint.
 *
 * Returns `null` if no entity by that name exists in the active org. Other
 * failures (auth, 5xx, etc.) propagate unchanged so callers can surface
 * them through their normal error path.
 */
export async function lookupEntityByIdOrName(
  client: IV2Client,
  idOrName: string,
): Promise<EntityKindInfo | null> {
  try {
    const resp = await client.get<{
      entity_id: string;
      name: string;
      display_name: string;
      kind: 'app' | 'agent';
      status?: string;
    }>(`/api/v2/runtimes/${encodeURIComponent(idOrName)}`);
    return {
      entity_id: resp.entity_id,
      name: resp.name,
      display_name: resp.display_name,
      realm: 'runtime',
      kind: resp.kind,
      status: resp.status,
    };
  } catch (err) {
    if (err instanceof V2ApiError && err.code === 'NOT_A_RUNTIME') {
      try {
        const insp = await client.get<{
          entity_id: string;
          name: string;
          display_name: string;
        }>(`/api/v2/inspirations/${encodeURIComponent(idOrName)}`);
        return {
          entity_id: insp.entity_id,
          name: insp.name,
          display_name: insp.display_name,
          realm: 'inspiration',
          kind: 'inspiration',
        };
      } catch (innerErr) {
        if (innerErr instanceof V2ApiError && innerErr.status === 404) return null;
        throw innerErr;
      }
    }
    if (err instanceof V2ApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * Per-verb redirect when the caller invoked a runtime-only verb against an
 * inspiration. Each clause is §23-voiced: plain, specific, points at one
 * concrete next step.
 */
export function inspirationRedirectFor(verb: string, name: string): string {
  switch (verb) {
    case 'unplug':
      return `To archive it: grid delete ${name}.`;
    case 'logs':
    case 'follow':
      return `Inspirations are statically served. Open it: grid open ${name}.`;
    case 'info':
    case 'status':
      return `To open it in your browser: grid open ${name}.`;
    case 'dev':
    case 'ssh':
    case 'pull':
      return `Inspirations don't have a runtime to ${verb} into. Open it: grid open ${name}.`;
    default:
      return `Open it: grid open ${name}.`;
  }
}

/**
 * Assert that `idOrName` resolves to a runtime entity. If it resolves to
 * an inspiration, throw a `CloudGridError` whose message includes the
 * per-verb redirect. Returns the lookup result so callers can reuse it
 * without re-fetching.
 *
 * Throws:
 *  - `CloudGridError` with the inspiration redirect when the entity is an
 *    inspiration.
 *  - `V2ApiError(404, 'RUNTIME_NOT_FOUND', ...)` when no entity by that
 *    name exists. The verb's normal error path renders this.
 *  - Anything else the API returned (auth, 5xx, etc.).
 */
export async function assertEntityIsRuntime(
  client: IV2Client,
  idOrName: string,
  verb: string,
): Promise<EntityKindInfo> {
  const info = await lookupEntityByIdOrName(client, idOrName);
  if (info === null) {
    throw new V2ApiError(
      404,
      'RUNTIME_NOT_FOUND',
      `No entity named '${idOrName}' in your active grid.`,
    );
  }
  if (info.realm !== 'runtime') {
    throw new CloudGridError(
      `${info.name} is an inspiration, not a runtime. ${inspirationRedirectFor(verb, info.name)}`,
    );
  }
  return info;
}
